import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  Image,
  Modal,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconInfoCircle,
  IconPencil,
  IconTrash,
  IconX,
} from "@tabler/icons-react";

import { ImageInfoPanel } from "../../clients/charts/ImageInfoPanel";
import type { ImageInfoModel } from "../../clients/charts/ImageInfoPanel";

/**
 * View-only carousel modal for a form ``image_upload`` entry.
 *
 * Header carries the icon strip (Pen, Info, Download, Delete, Close) +
 * an editable title. Pen forwards to ``onOpenMarkup``; the parent
 * (``ImageUploadField``) opens a separate fullscreen modal that hosts
 * ``ChartEditor`` directly — same surface the Documents tab and
 * Medical Charts use, no extra adornment.
 */

export interface FormImageViewerProps {
  opened: boolean;
  onClose: () => void;
  /** Blob URL of the image being viewed. */
  baseImageUrl: string;
  /** Initial title shown in the header (typically the filename). */
  title: string;
  /** Called whenever the user finishes editing the title. */
  onTitleChange?: (next: string) => void;

  /** Info-drawer model. Optional — without it the Info icon is hidden. */
  infoModel?: ImageInfoModel;

  /** Carousel navigation between images in the same field. */
  hasPrev?: boolean;
  hasNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;

  /** Download the current image as-is. */
  onDownload?: () => void;
  /** Soft-delete the current image. */
  onDelete?: () => void | Promise<void>;

  /**
   * Open the markup editor in a separate fullscreen modal. The icon
   * is hidden when not provided — happens when the entry hasn't been
   * materialized to a salon_documents row (legacy bare-string pointer).
   */
  onOpenMarkup?: () => void;
}

export function FormImageViewer({
  opened,
  onClose,
  baseImageUrl,
  title,
  onTitleChange,
  infoModel,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onDownload,
  onDelete,
  onOpenMarkup,
}: FormImageViewerProps) {
  const [infoOpen, setInfoOpen] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);

  // Sync the editable title to the prop when it changes (e.g. user
  // navigates prev/next between images, or markup save propagated a
  // new canonical title) — but don't clobber what the user is in the
  // middle of typing.
  useEffect(() => {
    setEditableTitle((cur) => (cur === title ? cur : title));
  }, [title]);

  // Keyboard nav between images.
  useEffect(() => {
    if (!opened) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && hasPrev) onPrev?.();
      if (e.key === "ArrowRight" && hasNext) onNext?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opened, hasPrev, hasNext, onPrev, onNext]);

  const commitTitle = useCallback(() => {
    if (editableTitle.trim() !== title) onTitleChange?.(editableTitle.trim());
  }, [editableTitle, title, onTitleChange]);

  const handleDelete = useCallback(async () => {
    await onDelete?.();
  }, [onDelete]);

  const iconStrip = useMemo(() => {
    const items: React.ReactNode[] = [];
    if (onOpenMarkup) {
      items.push(
        <Tooltip key="pen" label="Markup" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={onOpenMarkup}
            aria-label="Open markup editor"
          >
            <IconPencil size={18} />
          </ActionIcon>
        </Tooltip>,
      );
    }
    if (infoModel) {
      items.push(
        <Tooltip key="info" label="Image details" withArrow>
          <ActionIcon
            variant={infoOpen ? "filled" : "subtle"}
            color={infoOpen ? "blue" : "gray"}
            onClick={() => setInfoOpen((o) => !o)}
            aria-label="Toggle image details"
          >
            <IconInfoCircle size={18} />
          </ActionIcon>
        </Tooltip>,
      );
    }
    if (onDownload) {
      items.push(
        <Tooltip key="download" label="Download" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={onDownload}
            aria-label="Download image"
          >
            <IconDownload size={18} />
          </ActionIcon>
        </Tooltip>,
      );
    }
    if (onDelete) {
      items.push(
        <Tooltip key="delete" label="Remove" withArrow>
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={() => {
              void handleDelete();
            }}
            aria-label="Remove image"
          >
            <IconTrash size={18} />
          </ActionIcon>
        </Tooltip>,
      );
    }
    items.push(
      <Tooltip key="close" label="Close" withArrow>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onClose}
          aria-label="Close viewer"
        >
          <IconX size={18} />
        </ActionIcon>
      </Tooltip>,
    );
    return (
      <Group gap={4} wrap="nowrap" align="center">
        {items}
      </Group>
    );
  }, [
    onOpenMarkup,
    infoModel,
    infoOpen,
    onDownload,
    onDelete,
    onClose,
    handleDelete,
  ]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      closeOnClickOutside={false}
      padding={0}
    >
      <Flex direction="column" h="100vh">
        {/* Header — title left, icon strip right. */}
        <Group
          justify="space-between"
          wrap="nowrap"
          p="sm"
          style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}
        >
          <Group gap={4} wrap="nowrap" align="center">
            <TextInput
              variant="unstyled"
              value={editableTitle}
              onChange={(e) => setEditableTitle(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitTitle();
                  e.currentTarget.blur();
                }
              }}
              styles={{ input: { fontSize: 16, fontWeight: 600 } }}
              w={420}
              aria-label="Image title"
            />
            {editableTitle.trim() !== title &&
              editableTitle.trim().length > 0 && (
                <Tooltip label="Save title" withArrow>
                  <ActionIcon
                    variant="filled"
                    color="teal"
                    onClick={commitTitle}
                    aria-label="Save title"
                  >
                    <IconCheck size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
          </Group>
          {iconStrip}
        </Group>

        <Flex flex={1} mih={0}>
          <Box
            flex={1}
            style={{
              position: "relative",
              background: "var(--mantine-color-gray-1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <Image
              src={baseImageUrl}
              alt={title}
              fit="contain"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
            {hasPrev && (
              <ActionIcon
                variant="filled"
                color="dark"
                size="lg"
                radius="xl"
                style={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: 0.85,
                }}
                onClick={onPrev}
                aria-label="Previous image"
              >
                <IconChevronLeft size={22} />
              </ActionIcon>
            )}
            {hasNext && (
              <ActionIcon
                variant="filled"
                color="dark"
                size="lg"
                radius="xl"
                style={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: 0.85,
                }}
                onClick={onNext}
                aria-label="Next image"
              >
                <IconChevronRight size={22} />
              </ActionIcon>
            )}
          </Box>

          {infoOpen && infoModel && (
            <Box
              w={300}
              style={{
                borderLeft: "1px solid var(--mantine-color-gray-3)",
                background: "#fff",
                overflow: "auto",
              }}
            >
              <ImageInfoPanel model={infoModel} />
            </Box>
          )}
        </Flex>

        {(hasPrev || hasNext) && (
          <Group
            justify="center"
            p="xs"
            style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
          >
            <Text size="xs" c="dimmed">
              Use the arrows to flip between images.
            </Text>
          </Group>
        )}
      </Flex>
    </Modal>
  );
}
